import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, BarChart, Bar } from "recharts";
import { Users, FolderKanban, Search, Bell, CalendarCheck, Activity, TrendingUp, AlertTriangle } from "lucide-react";

function StatCard({ label, value, sub, icon: Icon, tone }) {
  const color = tone === "green" ? "text-emerald-600" : tone === "red" ? "text-red-600" : "text-[#0A0D14]";
  return (
    <div className="card-crisp p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-[#8A94A6]" />}
      </div>
      <div className={`mt-2 text-3xl font-display font-bold stat-value ${color}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-[#8A94A6]">{sub}</div>}
    </div>
  );
}

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/trends", { params: { days: 30 } }),
      api.get("/admin/recent-jobs", { params: { limit: 12 } }),
    ]).then(([s, t, j]) => { setStats(s.data); setTrends(t.data); setJobs(j.data); })
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load admin stats"));
  }, []);

  if (err) return <div className="p-10 text-red-600">{err}</div>;
  if (!stats) return <div className="p-10 text-[#8A94A6]">Loading platform stats…</div>;

  const planEntries = Object.entries(stats.users?.plans || {});
  const srcEntries = Object.entries(stats.keywords?.volume_source || {});
  const trendData = (key) => (trends?.[key] || []).map((d) => ({ day: d._id?.slice(5) || "", count: d.count }));

  return (
    <div className="p-8 lg:p-10" data-testid="admin-page">
      <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">Operator view</div>
      <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">Platform analytics.</h1>
      <p className="mt-2 text-sm text-[#525B6A] max-w-2xl">A live pulse of SerpNex — signups, tracking volume, jobs, alerts, and reports. Refresh the page for a live snapshot.</p>

      <section className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Users" value={stats.users.total} sub={`+${stats.users.new_7d} in last 7d`} icon={Users} />
        <StatCard label="Projects" value={stats.projects.total} sub={`${stats.projects.active_30d} active in 30d`} icon={FolderKanban} />
        <StatCard label="Keywords" value={stats.keywords.total} sub={`${stats.keywords.google} Google · ${stats.keywords.bing} Bing`} icon={Search} />
        <StatCard label="Alerts (7d)" value={stats.alerts.last_7d} sub={`${stats.alerts.total} total`} icon={Bell} />
        <StatCard label="Report schedules" value={stats.reports.schedules_active} sub={`${stats.reports.sent_30d} sent in 30d`} icon={CalendarCheck} />
        <StatCard label="Active share links" value={stats.share_links_active} sub="Not revoked" icon={Activity} />
        <StatCard label="Jobs (7d)" value={stats.jobs_7d.total} sub={`${stats.jobs_7d.failed} failed · ${stats.jobs_7d.enrichment_runs} enrichment`} icon={TrendingUp} tone={stats.jobs_7d.failed > 0 ? "red" : "green"} />
        <StatCard label="Blog posts" value={stats.blog.published} sub={`${stats.blog.draft} draft`} icon={Activity} />
      </section>

      <section className="mt-10 grid lg:grid-cols-2 gap-6">
        <div className="card-crisp p-6">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Signups · 30d</div>
            <div className="text-xs text-[#525B6A]">{stats.users.new_30d} new</div>
          </div>
          <div className="mt-4 h-40">
            <ResponsiveContainer><LineChart data={trendData("signups")}><XAxis dataKey="day" fontSize={10} stroke="#8A94A6" /><YAxis fontSize={10} stroke="#8A94A6" width={24} /><RTooltip /><Line dataKey="count" stroke="#0047FF" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer>
          </div>
        </div>
        <div className="card-crisp p-6">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Alerts · 30d</div>
            <div className="text-xs text-[#525B6A]">{stats.alerts.last_7d} in last 7d</div>
          </div>
          <div className="mt-4 h-40">
            <ResponsiveContainer><BarChart data={trendData("alerts")}><XAxis dataKey="day" fontSize={10} stroke="#8A94A6" /><YAxis fontSize={10} stroke="#8A94A6" width={24} /><RTooltip /><Bar dataKey="count" fill="#0047FF" /></BarChart></ResponsiveContainer>
          </div>
        </div>
        <div className="card-crisp p-6">
          <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium mb-3">Plan distribution</div>
          <div className="space-y-2">
            {planEntries.length === 0 && <div className="text-sm text-[#8A94A6]">No user data yet.</div>}
            {planEntries.map(([plan, n]) => (
              <div key={plan} className="flex items-center justify-between text-sm">
                <span className="capitalize text-[#0A0D14]">{plan}</span>
                <span className="tabular-nums text-[#525B6A]">{n}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card-crisp p-6">
          <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium mb-3">Search volume source</div>
          <div className="space-y-2">
            {srcEntries.map(([src, n]) => (
              <div key={src} className="flex items-center justify-between text-sm">
                <span className="capitalize text-[#0A0D14]">{src === "none" ? "Not enriched" : src}</span>
                <span className="tabular-nums text-[#525B6A]">{n}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-10 card-crisp overflow-hidden" data-testid="recent-jobs">
        <div className="px-6 py-3 border-b border-[#E2E8F0] bg-[#F8F9FA] flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Recent job runs</div>
          {stats.jobs_7d.failed > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-red-600"><AlertTriangle className="h-3 w-3" /> {stats.jobs_7d.failed} failures (7d)</span>}
        </div>
        {jobs.length === 0 && <div className="p-8 text-center text-[#8A94A6] text-sm">No job runs recorded yet.</div>}
        {jobs.map((j, i) => (
          <div key={i} className="grid grid-cols-12 items-center px-6 py-2.5 border-b border-[#E2E8F0] last:border-b-0 text-sm">
            <div className="col-span-2 text-[#0A0D14] font-medium capitalize">{j.kind?.replace('_', ' ')}</div>
            <div className="col-span-2"><span className={`pill ${j.status === 'complete' ? 'pill-success' : ''}`}>{j.status}</span></div>
            <div className="col-span-3 text-[#525B6A] text-xs tabular-nums">{Object.entries(j.counts || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}</div>
            <div className="col-span-2 text-[#525B6A] text-xs tabular-nums">{j.duration_ms ? `${j.duration_ms}ms` : "—"}</div>
            <div className="col-span-3 text-[#8A94A6] text-xs">{new Date(j.started_at).toLocaleString()}</div>
          </div>
        ))}
      </section>
    </div>
  );
}
